/**
 * NUVRYA shell runtime — Athora-parity behaviour for the global chrome.
 *
 * Everything here reproduces a *measured* behaviour, not a guessed one. The
 * numbers live in `nuvrya-tokens.css` so CSS and JS cannot drift apart, and the
 * measurement that produced each is cited at the call site.
 *
 * Sources: build/forensic/out/fx-header3.json, fx-header4.json, fx-corrections.json
 */

const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
const readPx = (name, fallback) => {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Reads a CSS time token as milliseconds.
 *
 * Shopify minifies theme CSS on upload, and `390ms` comes back as `.39s`.
 * A unit-blind parseFloat therefore hands WAAPI 0.39 — which it reads as
 * 0.39ms, so the animation is 1000x too fast and looks like no animation at
 * all. This normalises both spellings; measured live at 7ms before the fix.
 *
 * @param {string} name - custom property name
 * @param {number} fallback - milliseconds to use when the token is absent
 * @returns {number} milliseconds
 */
const readMs = (name, fallback) => {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return fallback;
  if (/ms\s*$/.test(v)) return n;
  if (/s\s*$/.test(v)) return n * 1000;
  return n;
};

/* =============================================================
 * Mega menu
 *
 * Measured dismissal rules (fx-corrections.json → megaDismiss):
 *   opens on hover ................................. true
 *   stays open with the pointer inside the panel ... true
 *   closes when the pointer leaves the region ...... true
 *   closes on scroll ............................... true
 *   closes on Escape ............................... FALSE in the reference
 *   closes on outside click ........................ FALSE in the reference
 *
 * The last two are accessibility defects, not features. Keyboard users must be
 * able to dismiss an overlay, so NUVRYA closes on Escape and on outside click.
 * This is a deliberate, documented divergence from the reference.
 * ============================================================= */
/**
 * The nearest ancestor that acts as the containing block for a `position: fixed`
 * descendant. Anything with a transform, perspective, filter, backdrop-filter,
 * a layout/paint containment or a will-change on those takes the job away from
 * the viewport — silently, and the only symptom is a fixed element positioned
 * against the wrong box.
 */
function fixedContainingBlock(element) {
  for (let node = element.parentElement; node && node !== document.documentElement; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (
      style.transform !== 'none' ||
      style.perspective !== 'none' ||
      style.filter !== 'none' ||
      (style.backdropFilter && style.backdropFilter !== 'none') ||
      /paint|layout|strict|content/.test(style.contain) ||
      /transform|filter|perspective/.test(style.willChange) ||
      (style.containerType && style.containerType !== 'normal')
    ) {
      return node;
    }
  }
  return null;
}

class NuvryaMegaMenu extends HTMLElement {
  #closeTimer = null;
  #panel = null;
  #trigger = null;
  #anim = null;
  #scrollTarget = null;
  #suppressFocusOpen = false;

  connectedCallback() {
    this.#panel = this.querySelector('[data-nuvrya-mega-panel]');
    this.#trigger = this.querySelector('[data-nuvrya-mega-trigger]');
    if (!this.#panel || !this.#trigger) return;

    this.addEventListener('pointerenter', this.#scheduleOpen);
    this.addEventListener('pointerleave', this.#scheduleClose);
    this.#trigger.addEventListener('focus', this.#onTriggerFocus);
    this.#trigger.addEventListener('keydown', this.#onTriggerKey);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('keydown', this.#onEscape);
    document.addEventListener('click', this.#onDocClick);
    this.#scrollTarget = window;
    this.#scrollTarget.addEventListener('scroll', this.#close, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onEscape);
    document.removeEventListener('click', this.#onDocClick);
    this.#scrollTarget?.removeEventListener('scroll', this.#close);
    clearTimeout(this.#closeTimer);
  }

  get open() {
    return this.hasAttribute('open');
  }

  #scheduleOpen = () => {
    clearTimeout(this.#closeTimer);
    this.#open();
  };

  // A short grace period keeps the panel alive while the pointer crosses the
  // gap between the trigger and the panel edge.
  #scheduleClose = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(this.#close, 120);
  };

  #open = () => {
    if (this.open) return;
    document.querySelectorAll('nuvrya-mega-menu[open]').forEach((menu) => {
      if (menu !== this) menu.close();
    });
    this.setAttribute('open', '');
    this.#trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('data-nuvrya-mega-open', '');

    // The large panels are fixed to the viewport so they can span its full
    // width the way the reference's do, which means their top edge cannot come
    // from the DOM — it is the header's bottom, measured at the moment of
    // opening so a scrolled or shrunken header still lines up.
    const root = document.documentElement;
    const header = this.closest('header-component, header') ?? this.closest('.shopify-section');
    if (header) {
      root.style.setProperty(
        '--nuvrya-shell-header-bottom',
        `${Math.round(header.getBoundingClientRect().bottom)}px`
      );
    }

    // …but "fixed to the viewport" is only true if nothing above it has taken
    // over as the containing block. The header pill bar carries a
    // `backdrop-filter`, which does exactly that, so the panel's 15px inset was
    // being measured from the header row. Measure the offender and hand CSS the
    // correction; see the note on `.nuvrya-mega__panel` in nuvrya-shell.css.
    const cb = fixedContainingBlock(this.#panel);
    let startOffset = 0;
    let endOffset = 0;
    let topOffset = 0;
    if (cb) {
      const box = cb.getBoundingClientRect();
      const fromLeft = box.left;
      const fromRight = root.clientWidth - box.right;
      const rtl = getComputedStyle(root).direction === 'rtl';
      startOffset = rtl ? fromRight : fromLeft;
      endOffset = rtl ? fromLeft : fromRight;
      topOffset = box.top;
    }
    root.style.setProperty('--nuvrya-mega-cb-start', `${startOffset}px`);
    root.style.setProperty('--nuvrya-mega-cb-end', `${endOffset}px`);
    root.style.setProperty('--nuvrya-mega-cb-y', `${topOffset}px`);

    const duration = RM.matches ? 1 : readMs('--nuvrya-mega-duration', 390);
    const offset = RM.matches ? 0 : readPx('--nuvrya-mega-offset', 15);
    this.#anim?.cancel();
    this.#anim = this.#panel.animate(
      [
        { opacity: 0, transform: `translateY(${offset}px)` },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration, easing: 'cubic-bezier(0, 0, 0.58, 1)', fill: 'both' }
    );
    this.dispatchEvent(new CustomEvent('nuvrya:mega:open', { bubbles: true }));
  };

  #close = () => {
    if (!this.open) return;
    clearTimeout(this.#closeTimer);
    this.removeAttribute('open');
    this.#trigger.setAttribute('aria-expanded', 'false');
    if (!document.querySelector('nuvrya-mega-menu[open]')) {
      document.documentElement.removeAttribute('data-nuvrya-mega-open');
    }
    const duration = RM.matches ? 1 : readMs('--nuvrya-mega-duration', 390) * 0.6;
    this.#anim?.cancel();
    this.#anim = this.#panel.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration, easing: 'ease-in', fill: 'both' }
    );
    this.dispatchEvent(new CustomEvent('nuvrya:mega:close', { bubbles: true }));
  };

  #onTriggerKey = (e) => {
    if (e.key === 'ArrowDown' && this.open) {
      e.preventDefault();
      this.#panel.querySelector('a, button')?.focus();
    }
  };

  #onTriggerFocus = () => {
    if (this.#suppressFocusOpen) return;
    this.#open();
  };

  // Divergence from reference: Escape dismisses. Required for WCAG 2.1.1/1.4.13.
  #onEscape = (e) => {
    if (e.key !== 'Escape' || !this.open) return;
    this.#suppressFocusOpen = true;
    this.#close();
    this.#trigger.focus();
    queueMicrotask(() => {
      this.#suppressFocusOpen = false;
    });
  };

  // Divergence from reference: an outside click dismisses.
  #onDocClick = (e) => {
    if (this.open && !this.contains(e.target)) this.#close();
  };

  #onFocusOut = (e) => {
    if (this.open && !this.contains(e.relatedTarget)) this.#close();
  };

  close() {
    this.#close();
  }
}
if (!customElements.get('nuvrya-mega-menu')) customElements.define('nuvrya-mega-menu', NuvryaMegaMenu);

/* =============================================================
 * Pointer-following local fill
 *
 * The reference sets `hdt-links-cursor--false` on <html>: there is no global
 * custom cursor. The effect is entirely local to a button — a circle that
 * grows from wherever the pointer entered. Writing two custom properties and
 * letting CSS own the visual keeps this off the main thread.
 * ============================================================= */
class NuvryaFillButton extends HTMLElement {
  connectedCallback() {
    if (RM.matches) return;
    // A coarse pointer has no hover, so the effect would only ever fire on tap.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.addEventListener('pointerenter', this.#track);
    this.addEventListener('pointerleave', this.#track);
  }

  #track = (e) => {
    const r = this.getBoundingClientRect();
    this.style.setProperty('--nuvrya-fill-x', `${((e.clientX - r.left) / r.width) * 100}%`);
    this.style.setProperty('--nuvrya-fill-y', `${((e.clientY - r.top) / r.height) * 100}%`);
  };
}
if (!customElements.get('nuvrya-fill-button')) customElements.define('nuvrya-fill-button', NuvryaFillButton);

const pointerFillSelector = '.button:not(.button-unstyled), .button-secondary, .button-custom';
if (!RM.matches && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  document.addEventListener('pointermove', (event) => {
    const button = event.target.closest(pointerFillSelector);
    if (!button) return;
    const bounds = button.getBoundingClientRect();
    button.style.setProperty('--nuvrya-fill-x', `${event.clientX - bounds.left}px`);
    button.style.setProperty('--nuvrya-fill-y', `${event.clientY - bounds.top}px`);
  }, { passive: true });
}

/* =============================================================
 * Mobile drill-down menu
 *
 * Measured: a single left dialog at 100% width holding stacked panels; the
 * openers carry the target panel id. Panels translate as a set, so only one
 * transform is animated no matter how deep the stack goes.
 * ============================================================= */
class NuvryaDrilldown extends HTMLElement {
  #stack = [];
  #track = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-nuvrya-drill-track]');
    this.addEventListener('click', this.#onClick);
  }

  #onClick = (e) => {
    const opener = e.target.closest('[data-nuvrya-drill-open]');
    if (opener) {
      e.preventDefault();
      this.#push(opener.getAttribute('data-nuvrya-drill-open'));
      return;
    }
    if (e.target.closest('[data-nuvrya-drill-back]')) {
      e.preventDefault();
      this.#pop();
    }
  };

  #push(id) {
    const panel = this.querySelector(`[data-nuvrya-drill-panel="${CSS.escape(id)}"]`);
    if (!panel) return;
    this.#stack.push(panel);
    this.#render();
    // Move focus into the panel that just became the active one.
    panel.querySelector('a, button')?.focus({ preventScroll: true });
  }

  #pop() {
    const leaving = this.#stack.pop();
    this.#render();
    const opener = this.querySelector(`[data-nuvrya-drill-open="${CSS.escape(leaving?.dataset.nuvryaDrillPanel ?? '')}"]`);
    opener?.focus({ preventScroll: true });
  }

  #render() {
    const depth = this.#stack.length;
    this.dataset.depth = String(depth);
    if (this.#track) this.#track.style.setProperty('--nuvrya-drill-depth', String(depth));
    this.querySelectorAll('[data-nuvrya-drill-panel]').forEach((p) => {
      const active = this.#stack[this.#stack.length - 1] === p;
      const onStack = this.#stack.includes(p);
      p.toggleAttribute('data-active', active);
      // Everything not currently reachable is removed from the a11y tree, so
      // a screen reader never walks into a panel the user cannot see.
      p.toggleAttribute('inert', !active);
      p.setAttribute('aria-hidden', active ? 'false' : 'true');
      p.toggleAttribute('data-on-stack', onStack);
    });
    const root = this.querySelector('[data-nuvrya-drill-root]');
    if (root) {
      root.toggleAttribute('inert', depth > 0);
      root.setAttribute('aria-hidden', depth > 0 ? 'true' : 'false');
    }
  }

  reset() {
    this.#stack = [];
    this.#render();
  }
}
if (!customElements.get('nuvrya-drilldown')) customElements.define('nuvrya-drilldown', NuvryaDrilldown);

/* =============================================================
 * Mobile navigation dialog
 *
 * Athora uses a native modal drawer at full viewport width with a 15px
 * inset surface. Keeping that architecture here gives us native focus
 * containment and Escape handling while the drill-down owns only navigation.
 * ============================================================= */
class NuvryaMobileMenu extends HTMLElement {
  #dialog = null;
  #opener = null;
  #surface = null;
  #closing = false;
  #animation = null;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-nuvrya-mobile-dialog]');
    this.#opener = this.querySelector('[data-nuvrya-mobile-open]');
    this.#surface = this.querySelector('[data-nuvrya-mobile-surface]');
    if (!this.#dialog || !this.#opener || !this.#surface) return;

    this.#opener.addEventListener('click', this.#open);
    this.querySelectorAll('[data-nuvrya-mobile-close]').forEach((button) => {
      button.addEventListener('click', this.#close);
    });
    this.#dialog.addEventListener('cancel', this.#onCancel);
    this.#dialog.addEventListener('click', this.#onBackdropClick);
    this.#dialog.addEventListener('close', this.#cleanup);
  }

  disconnectedCallback() {
    this.#animation?.cancel();
    this.#animation = null;
    if (this.#dialog?.open) this.#dialog.close();
    document.documentElement.removeAttribute('data-nuvrya-mobile-menu-open');
    this.#opener?.removeEventListener('click', this.#open);
    this.#dialog?.removeEventListener('cancel', this.#onCancel);
    this.#dialog?.removeEventListener('click', this.#onBackdropClick);
    this.#dialog?.removeEventListener('close', this.#cleanup);
  }

  #open = () => {
    if (this.#dialog.open) return;
    this.#animation?.cancel();
    this.#animation = null;
    this.#closing = false;
    this.#dialog.showModal();
    this.#opener.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('data-nuvrya-mobile-menu-open', '');
    const duration = RM.matches ? 1 : 360;
    this.#animation = this.#surface.animate(
      [
        { opacity: 0, transform: 'translateX(-24px)' },
        { opacity: 1, transform: 'translateX(0)' },
      ],
      { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
    this.querySelector('[data-nuvrya-mobile-close]')?.focus({ preventScroll: true });
  };

  #close = () => {
    if (!this.#dialog.open || this.#closing) return;
    this.#closing = true;
    this.#animation?.cancel();
    const duration = RM.matches ? 1 : 220;
    const animation = this.#surface.animate(
      [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: 'translateX(-20px)' },
      ],
      { duration, easing: 'ease-in', fill: 'forwards' }
    );
    this.#animation = animation;
    animation.finished.then(() => {
      // Ignore a stale close after disconnection or a new animation.
      if (this.#animation !== animation) return;
      if (this.#dialog.open) this.#dialog.close();
      this.#cleanup();
    }, () => {});
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #onBackdropClick = (event) => {
    if (event.target === this.#dialog) this.#close();
  };

  #cleanup = () => {
    if (this.#dialog?.open) return;
    // A forwards-filled closing animation otherwise leaves opacity at zero
    // after the dialog is reopened (and keeps its modal backdrop trapping input).
    this.#animation?.cancel();
    this.#animation = null;
    this.#closing = false;
    this.#opener.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('data-nuvrya-mobile-menu-open');
    this.querySelector('nuvrya-drilldown')?.reset();
    this.#opener.focus({ preventScroll: true });
  };
}
if (!customElements.get('nuvrya-mobile-menu')) customElements.define('nuvrya-mobile-menu', NuvryaMobileMenu);

/* =============================================================
 * Animated typing placeholder
 *
 * Measured: <hdt-typing data-text='["…","…"]'> cycles two strings. It writes
 * into the input's placeholder, so the field stays a real, labelled input.
 * ============================================================= */
class NuvryaTyping extends HTMLElement {
  #timer = null;
  #i = 0;
  #chars = 0;
  #dir = 1;

  connectedCallback() {
    try {
      this.phrases = JSON.parse(this.getAttribute('data-text') || '[]');
    } catch {
      this.phrases = [];
    }
    this.input = document.getElementById(this.getAttribute('for') || '');
    if (!this.input || this.phrases.length === 0) return;
    // Reduced motion gets the first phrase, statically. No typing loop at all.
    if (RM.matches) {
      this.input.placeholder = this.phrases[0];
      return;
    }
    this.#tick();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #tick = () => {
    const phrase = this.phrases[this.#i];
    this.#chars += this.#dir;
    this.input.placeholder = phrase.slice(0, this.#chars);

    let delay = this.#dir > 0 ? 55 : 28;
    if (this.#chars >= phrase.length) {
      this.#dir = -1;
      delay = 1800; // hold the complete phrase
    } else if (this.#chars <= 0) {
      this.#dir = 1;
      this.#i = (this.#i + 1) % this.phrases.length;
      delay = 320;
    }
    this.#timer = setTimeout(this.#tick, delay);
  };
}
if (!customElements.get('nuvrya-typing')) customElements.define('nuvrya-typing', NuvryaTyping);

/* =============================================================
 * Return to top
 * ============================================================= */
class NuvryaReturnTop extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button') || this;
    this.button.addEventListener('click', () => {
      // Honour the OS setting: an instant jump for users who asked for less motion.
      const scroller = window;
      scroller?.scrollTo({ top: 0, behavior: RM.matches ? 'auto' : 'smooth' });
      document.querySelector('#MainContent')?.focus?.({ preventScroll: true });
    });
    this.#io = new IntersectionObserver(
      ([entry]) => this.toggleAttribute('data-visible', !entry.isIntersecting),
      { rootMargin: '0px 0px -100% 0px' }
    );
    const sentinel = document.querySelector('[data-nuvrya-top-sentinel]') || document.body.firstElementChild;
    if (sentinel) this.#io.observe(sentinel);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
  }

  #io = null;
}
if (!customElements.get('nuvrya-return-top')) customElements.define('nuvrya-return-top', NuvryaReturnTop);

export { NuvryaMegaMenu, NuvryaFillButton, NuvryaDrilldown, NuvryaTyping, NuvryaReturnTop };

/* ------------------------------------------------------------------
 * Mega-menu card rail arrows
 *
 * The reference pins a 35px prev/next pair to the ends of the view-all row and
 * dims, rather than hides, the one with nowhere to go. The rail itself is a
 * plain scroll container, so the arrows only have to move it by one card plus
 * the gap and keep their own disabled state in sync.
 * ---------------------------------------------------------------- */
function wireMegaCardRails(root = document) {
  for (const row of root.querySelectorAll?.('.nuvrya-mega__viewall') ?? []) {
    if (row.dataset.nuvryaRailWired) continue;
    const rail = row.parentElement?.querySelector('.nuvrya-mega__cards');
    const prev = row.querySelector('[data-nuvrya-cards-prev]');
    const next = row.querySelector('[data-nuvrya-cards-next]');
    if (!rail || !prev || !next) continue;
    row.dataset.nuvryaRailWired = 'true';

    const step = () => {
      const card = rail.firstElementChild;
      if (!card) return rail.clientWidth;
      const gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const sync = () => {
      // A one-pixel tolerance: scrollLeft is fractional after a smooth scroll,
      // so an exact comparison leaves the end arrow enabled forever.
      prev.disabled = rail.scrollLeft <= 1;
      next.disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1;
    };

    prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    rail.addEventListener('scroll', sync, { passive: true });
    new ResizeObserver(sync).observe(rail);
    sync();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => wireMegaCardRails(), { once: true });
} else {
  wireMegaCardRails();
}
